/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, {useRef} from 'react';
import {WithTranslationProps} from 'react-i18next';
import {AccessibilityInfo, Platform, View} from 'react-native';
import {useSelector} from 'react-redux';
import {useNavigation} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {get, noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import ModalHeader from '/component/layout/ModalHeader';
import NavigationHeader from '/component/layout/NavigationHeader';
import TabLayout from '/component/layout/TabLayout';
import InfoPopover from '/component/partial/InfoPopover';
import {CLIENT_PROFILE, COACH_PROFILE} from '/enum/recordType.enum';
import {EVENT_NAMES, SCREEN_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {createPopoverProvider} from '/provider/PopoverProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {ContactActionPopover} from './ProfileTab/Contact';
import {styles} from './CoachProfile.style';
import EditNotes from './EditNotes';
import Header from './Header';
import OrderDetail from './OrderDetail';
import OrderHistoryList from './OrderHistoryList';
import PerformanceTab from './PerformanceTab';
import ProfileTab from './ProfileTab';
import TeamsTab from './TeamsTab';

interface CoachProfileProps extends WithTranslationProps {
  fullName: string;
  recordId: number | null;
  onClose: () => void;
  onShowRecord: (arg: {recordType: string; recordId: number}) => void;
  hasData: boolean;
  fetchData: () => void;
  lastPaidRank: string;
  hasBusinessBuilderBonusContributors: boolean;
  showBusinessBuilderBonusProfileCard: boolean;
  reportName: string;
  ts: (key: string, defaultValue: string) => string;
  tt: (key: string, defaultValue: string) => string;
}
export class CoachProfile extends React.PureComponent<CoachProfileProps> {
  popoverProvider: any;

  constructor(props: CoachProfileProps) {
    super(props);
    this.popoverProvider = createPopoverProvider();
  }

  override componentDidMount(): void {
    const {
      hasData = false,
      fetchData = noop,
      ts = () => 'loading',
    } = this.props;

    if (!hasData) {
      AccessibilityInfo.announceForAccessibility(ts('loading', 'Loading'));
      fetchData();
    }
  }

  renderLoading() {
    const {onClose = noop} = this.props;

    return (
      <ModalHeader
        isProfileScreen
        closeIconName="chevron-left"
        onClose={onClose}
        style={{base: styles.base}}>
        <View style={[styles.content, styles.contentLoading]}>
          <LoadingIndicator size="large" />
        </View>
      </ModalHeader>
    );
  }

  getTab(BaseComponent: any, componentProps = {}) {
    const {Consumer: PopoverConsumer} = this.popoverProvider;
    const {
      recordId = null,
      onShowRecord = noop,
      reportName,
      hasBusinessBuilderBonusContributors,
      showBusinessBuilderBonusProfileCard,
    } = this.props;

    return () => {
      const TabComponent = ({popoverShow}: any) => {
        const navigation = useNavigation();
        const handleShowInfoPopoverRef = useRef((payload: any) =>
          popoverShow(InfoPopover, {
            ...payload,
            useModal: Platform.OS === 'android',
          }),
        );

        const handleOrderPressRef = useRef(({orderId}: any) => {
          navigation.push('coach-profile-order-detail-screen', {orderId});
        });

        return (
          <BaseComponent
            {...componentProps}
            coachId={recordId}
            onShowRecord={onShowRecord}
            onShowInfoPopover={handleShowInfoPopoverRef.current}
            onOrderPress={handleOrderPressRef.current}
            popoverShow={popoverShow}
            hasBusinessBuilderBonusContributors={
              hasBusinessBuilderBonusContributors
            }
            showBusinessBuilderBonusProfileCard={
              showBusinessBuilderBonusProfileCard
            }
          />
        );
      };

      return (
        <PopoverConsumer>
          {({popoverShow}: any) => <TabComponent popoverShow={popoverShow} />}
        </PopoverConsumer>
      );
    };
  }

  getMainScreenComponent() {
    const {Consumer: PopoverConsumer} = this.popoverProvider;
    const {
      fullName = '',
      onClose = noop,
      recordId = null,
      ts = () => '',
      lastPaidRank = '',
    } = this.props;

    return ({navigation: {navigate}}: any) => {
      const screenComponent = useRef(({popoverShow}: any) => {
        const handleViewMoreOrderHistoryRef = useRef(() => {
          getTealiumUtilDefaultInstance().generateTrackEvent({
            eventName: EVENT_NAMES.onPageLoad,
          })({
            currentScreenName: SCREEN_NAMES.orderHistoryList,
            previousScreenName: SCREEN_NAMES.coachProfile,
            lastPaidRank,
          });
          navigate('coach-profile-order-history-list-screen');
        });

        const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
          eventName: EVENT_NAMES.profileTabClick,
        });

        const generateOnTabPress = ({tabName}: any) => {
          sendEvent({
            userType: USER_TYPES.coach,
            userId: recordId,
            userFullName: fullName,
            tabName,
            lastPaidRank,
          });
        };

        const handleShowContactActions = useRef(() => {
          return () => popoverShow(ContactActionPopover, {coachId: recordId});
        });

        const featureFlag = useSelector(variablesFeatureFlag);
        const removeExpiringRewards = shouldReleaseFeature(
          get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
        );

        return (
          <ModalHeader
            isProfileScreen
            onClose={onClose}
            closeIconName="chevron-left"
            title={ts('coachProfile', 'Coach profile')}
            style={{base: styles.base}}>
            <Header coachId={recordId} />
            <View style={styles.content}>
              <TabLayout
                tabs={[
                  {
                    name: 'coach-profile-profile-tab',
                    label: ts('ProfileTabLabel', 'Profile'),
                    component: this.getTab(ProfileTab, {
                      onViewMoreOrderHistory:
                        handleViewMoreOrderHistoryRef.current,
                      onShowContactActions: handleShowContactActions.current,
                      navigate,
                      recordId,
                      removeExpiringRewards,
                    }),
                    onTabPress: () => generateOnTabPress({tabName: 'Profile'}),
                  },
                  {
                    name: 'coach-profile-performance-tab',
                    label: ts('PerformanceTabLabel', 'Performance'),
                    component: this.getTab(PerformanceTab, {recordId}),
                    onTabPress: () =>
                      generateOnTabPress({tabName: 'Performance'}),
                  },
                  {
                    name: 'coach-profile-teams-tab',
                    label: ts('TeamsTabLabel', 'Teams'),
                    component: this.getTab(TeamsTab),
                    onTabPress: () => generateOnTabPress({tabName: 'Teams'}),
                  },
                ]}
                style={{tabBarStyle: styles.tabLayout}}
              />
            </View>
          </ModalHeader>
        );
      });

      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const ScreenComponent = screenComponent.current;
            return <ScreenComponent popoverShow={popoverShow} />;
          }}
        </PopoverConsumer>
      );
    };
  }

  getOrderHistoryListScreenComponent() {
    const {Consumer: PopoverConsumer} = this.popoverProvider;
    const {
      recordId = null,
      fullName = '',
      ts = (_key: string, fallback: string) => fallback,
    } = this.props;

    return ({navigation: {push, goBack}}: any) => {
      const handleOrderPressRef = useRef(({orderId}: any) => {
        push('coach-profile-order-detail-screen', {orderId});
      });
      const handleBackPressRef = useRef(() => {
        goBack();
      });
      const orderHistoryTitle = fullName
        ? ts('orderHistory.titleWithName', `${fullName}'s Order History`)
        : ts('orderHistory.title', 'Order History');

      return (
        <PopoverConsumer>
          {() => (
            <NavigationHeader style={{base: styles.base}} showBack={false}>
              <View style={styles.content}>
                <OrderHistoryList
                  coachId={recordId}
                  onOrderPress={handleOrderPressRef.current}
                  onBackPress={handleBackPressRef.current}
                  headerTitle={orderHistoryTitle}
                />
              </View>
            </NavigationHeader>
          )}
        </PopoverConsumer>
      );
    };
  }

  getOrderDetailScreenComponent() {
    const {
      recordId = null,
      onShowRecord = noop,
      lastPaidRank = '',
    } = this.props;

    return ({route: {params: {orderId} = {}}, navigation: {navigate}}: any) => {
      const handleViewProfileRef = useRef(({clientId, coachId}: any) => {
        if ((clientId || coachId) === recordId) {
          navigate('coach-profile-main-screen');
        } else {
          if (clientId) {
            onShowRecord({recordType: CLIENT_PROFILE, recordId: clientId});
          } else if (coachId) {
            onShowRecord({recordType: COACH_PROFILE, recordId: coachId});
          }
        }
      });

      const handleViewOrderHistoryRef = useRef(() => {
        navigate('coach-profile-order-history-list-screen');
      });

      return (
        <NavigationHeader style={{base: styles.base}}>
          <View style={styles.content}>
            <OrderDetail
              orderId={orderId}
              onViewProfile={handleViewProfileRef.current}
              onViewOrderHistory={handleViewOrderHistoryRef.current}
            />
          </View>
        </NavigationHeader>
      );
    };
  }

  EditNotesComponent = () => {
    return () => {
      const {recordId = null} = this.props;
      return <EditNotes recordId={recordId} />;
    };
  };

  getProfileStackComponent() {
    const ProfileStackNavigator = createStackNavigator();
    const MainScreen = this.getMainScreenComponent();
    const OrderHistoryListScreen = this.getOrderHistoryListScreenComponent();
    const OrderDetailScreen = this.getOrderDetailScreenComponent();
    const EditNotesScreen = this.EditNotesComponent();

    return () => (
      <ProfileStackNavigator.Navigator
        initialRouteName="coach-profile-main-screen"
        screenOptions={{headerShown: false}}>
        <ProfileStackNavigator.Screen
          name="coach-profile-main-screen"
          component={MainScreen}
        />
        <ProfileStackNavigator.Screen
          name="coach-profile-order-history-list-screen"
          component={OrderHistoryListScreen}
        />
        <ProfileStackNavigator.Screen
          name="coach-profile-order-detail-screen"
          component={OrderDetailScreen}
        />
        <ProfileStackNavigator.Screen
          name="coach-notes-edit-screen"
          component={EditNotesScreen}
        />
      </ProfileStackNavigator.Navigator>
    );
  }

  override render() {
    const {hasData = false} = this.props;
    const {Provider: PopoverProvider} = this.popoverProvider;
    const ProfileStack = this.getProfileStackComponent();

    return (
      <PopoverProvider>
        {!hasData ? (
          this.renderLoading()
        ) : (
          <KeyboardAvoidingView>
            <ProfileStack />
          </KeyboardAvoidingView>
        )}
      </PopoverProvider>
    );
  }
}

export default withTranslation('screen.coachProfile')(CoachProfile);
